import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import config from "../config";
import axios from "axios";

const Bookings = () => {
  const currentDate = new Date();
  const ninetyDaysAgo = new Date(currentDate);
  ninetyDaysAgo.setDate(currentDate.getDate() - 90);

  const [bookings, setBookings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [filterOption, setFilterOption] = useState("");
  const [fromDate, setFromDate] = useState(
    ninetyDaysAgo.toISOString().split("T")[0]
  );
  const [toDate, setToDate] = useState(currentDate.toISOString().split("T")[0]);
  const [statusFilter, setStatusFilter] = useState("");
  const [totalBookings, setTotalBookings] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Initial data fetch when the component is first rendered
  useEffect(() => {
    fetchBookings(1); 
    // eslint-disable-next-line
  }, []);
  // eslint-disable-next-line

  const fetchBookings = async (page = 1) => {
    if (!fromDate || !toDate) return;

    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/api/book/bookings`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          params: {
            page,
            limit: 10,
            ...(searchText && filterOption && { searchText, filterOption }),
            ...(statusFilter && { statusFilter }),
            fromDate,
            toDate,
          },
        }
      );

      setBookings(response.data.bookings);
      setTotalBookings(response.data.totalCount);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const handleSearchSubmit = () => {
    setCurrentPage(1);
    fetchBookings(1);
    setShowFilters(false); // Close filters on mobile after search
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchBookings(pageNumber);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'payment due': return 'bg-yellow-100 text-yellow-800';
      case 'review': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-800 text-white">
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Mobile Header */}
        <div className="lg:hidden bg-gray-900 p-4 flex justify-between items-center">
          <button 
            onClick={toggleSidebar}
            className="text-white hover:text-gray-300"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={1.5} 
              stroke="currentColor" 
              className="w-6 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
          <h1 className="text-lg font-bold">Bookings</h1>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="text-white hover:text-gray-300"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={1.5} 
              stroke="currentColor" 
              className="w-6 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
            </svg>
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-6">
          {/* Desktop Header */}
          <h1 className="hidden lg:block text-3xl font-bold mb-6">All Bookings</h1>
          
          {/* Filters Section */}
          <div className={`mb-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-gray-700 lg:bg-transparent p-4 lg:p-0 rounded-lg lg:rounded-none">
              {/* Mobile Filter Header */}
              <div className="flex justify-between items-center mb-4 lg:hidden">
                <h2 className="text-lg font-semibold">Filters</h2>
                <button 
                  onClick={() => setShowFilters(false)}
                  className="text-gray-300 hover:text-white"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {/* Filter Type */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium">Filter By:</label>
                  <select
                    value={filterOption}
                    onChange={(e) => setFilterOption(e.target.value)}
                    className="p-2 rounded text-black text-sm"
                  >
                    <option value="">Select Filter</option>
                    <option value="customerName">Customer Name</option>
                    <option value="orderId">Order ID</option>
                    <option value="visa">Country Visa</option>
                  </select>
                </div>

                {/* Search Text */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium">Search:</label>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Enter search text"
                    className="p-2 rounded text-black text-sm"
                  />
                </div>

                {/* Status Filter */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium">Status:</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="p-2 rounded text-black text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="processing">Processing</option>
                    <option value="Payment Due">Payment Due</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="Review">Review</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                {/* From Date */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium">From Date:</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="p-2 rounded text-black text-sm"
                  />
                </div>

                {/* To Date */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium">To Date:</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="p-2 rounded text-black text-sm"
                  />
                </div>

                {/* Search Button */}
                <div className="flex flex-col justify-end">
                  <button
                    onClick={handleSearchSubmit}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm transition-colors"
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full bg-white text-black rounded-lg overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 text-left">BookingID</th>
                  <th className="py-3 px-4 text-left">OrderID</th>
                  <th className="py-3 px-4 text-left">Customer Name</th>
                  <th className="py-3 px-4 text-left">Visa Type</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-left">Plan</th>
                  <th className="py-3 px-4 text-left">Date</th>
                  <th className="py-3 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking, index) => (
                  <tr key={booking.bookid} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-3 px-4">{booking.bookid}</td>
                    <td className="py-3 px-4">{booking.bid}</td>
                    <td className="py-3 px-4">{booking.customer_name}</td>
                    <td className="py-3 px-4">{booking.visatype}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">{booking.plan}</td>
                    <td className="py-3 px-4">
                      {new Date(booking.createdon).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <a href={`/bookings/${booking.bookid}`}>
                        <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded text-sm transition-colors">
                          View
                        </button>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            {bookings.map((booking) => (
              <div key={booking.bookid} className="bg-white text-black rounded-lg p-4 shadow">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-semibold text-lg">{booking.customer_name}</p>
                    <p className="text-sm text-gray-600">ID: {booking.bookid}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                  <div>
                    <p className="text-gray-600">Order ID</p>
                    <p className="font-medium">{booking.bid}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Visa Type</p>
                    <p className="font-medium">{booking.visatype}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Plan</p>
                    <p className="font-medium">{booking.plan}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Date</p>
                    <p className="font-medium">{new Date(booking.createdon).toLocaleDateString()}</p>
                  </div>
                </div>
                
                <a href={`/bookings/${booking.bookid}`} className="block">
                  <button className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition-colors">
                    View Details
                  </button>
                </a>
              </div>
            ))}
          </div>

          {/* Results Info */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
            <div className="text-sm lg:text-base">
              Showing {bookings.length} of {totalBookings} bookings
            </div>
            
            {/* Pagination */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="bg-gray-500 hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-3 lg:px-4 rounded text-sm transition-colors"
              >
                Prev
              </button>
              <span className="mx-2 text-sm lg:text-base">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="bg-gray-500 hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-3 lg:px-4 rounded text-sm transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Bookings;